import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Store, Search, MapPin, Instagram, Globe, MessageCircle } from "lucide-react";
import { formatRupiah } from "@/lib/format";
import { toast } from "sonner";

const CATEGORIES = ["Semua", "WO", "Fotografer", "MUA", "Catering", "Venue", "Dekorasi", "Musik", "Suvenir", "Lainnya"];

export default function VendorPilihan() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [q, setQ] = useState("");
  const [kategori, setKategori] = useState("Semua");
  const [sort, setSort] = useState("nama");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/public/vendor-listing"); setVendors(data); }
      catch { toast.error("Gagal memuat vendor"); }
      finally { setLoading(false); }
    })();
  }, []);

  const canAccess = user?.plan === "premium" || user?.role === "sysadmin" || user?.role === "Admin";
  if (!canAccess) {
    return (
      <div>
        <PageTitle title="Vendor Pilihan" subtitle="Katalog vendor terkurasi untuk pernikahan Anda" icon={Store} />
        <Card className="p-8 text-center">
          <Store className="mx-auto h-10 w-10 text-primary" />
          <p className="mt-3 font-semibold">Fitur Eksklusif Paket Premium</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Upgrade ke Paket Premium untuk mengakses direktori vendor pilihan (WO, Fotografer, MUA, Catering, dll).
          </p>
          <Button className="mt-4" onClick={() => window.location.assign("/paket")} data-testid="vendor-upgrade-btn">
            Lihat Paket Premium
          </Button>
        </Card>
      </div>
    );
  }

  const filtered = vendors
    .filter((v) => (kategori === "Semua" || v.kategori === kategori))
    .filter((v) => !q || `${v.nama_vendor} ${v.kota} ${v.kategori}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => {
      if (sort === "harga") return (a.harga_mulai || 0) - (b.harga_mulai || 0);
      if (sort === "kota") return (a.kota || "").localeCompare(b.kota || "");
      if (sort === "kategori") return (a.kategori || "").localeCompare(b.kategori || "");
      return (a.nama_vendor || "").localeCompare(b.nama_vendor || "");
    });

  return (
    <div>
      <PageTitle title="Vendor Pilihan" subtitle={`${vendors.length} vendor terkurasi`} icon={Store} />

      <Card className="mb-5 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="vendor-search" value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-9" placeholder="Cari nama vendor, kota, kategori…" />
          </div>
          <select data-testid="vendor-filter-kategori" value={kategori} onChange={(e) => setKategori(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="vendor-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm">
            <option value="nama">Urut: Nama</option>
            <option value="kategori">Urut: Kategori</option>
            <option value="kota">Urut: Kota</option>
            <option value="harga">Urut: Harga terendah</option>
          </select>
        </div>
      </Card>

      {loading ? <p className="text-sm text-muted-foreground">Memuat…</p> :
        filtered.length === 0 ? <EmptyState text="Tidak ada vendor sesuai filter." /> : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((v) => {
            const waLink = v.whatsapp ? `https://wa.me/${v.whatsapp}?text=${encodeURIComponent(`Halo ${v.nama_vendor}, saya tertarik dengan layanan Anda. Info lebih lengkap dari Wedifylab.`)}` : "";
            return (
              <Card key={v.id} data-testid={`vendor-card-${v.id}`} className="flex flex-col overflow-hidden">
                {v.cover && <div className="relative h-40 overflow-hidden bg-muted">
                  <img src={v.cover} alt={v.nama_vendor} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
                  {v.logo && <div className="absolute bottom-2 left-2 h-12 w-12 overflow-hidden rounded-full border-2 border-white bg-white shadow-lg">
                    <img src={v.logo} alt="" className="h-full w-full object-cover" />
                  </div>}
                  <Badge className="absolute right-2 top-2">{v.kategori}</Badge>
                </div>}
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-heading text-lg font-semibold">{v.nama_vendor}</h3>
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{v.deskripsi}</p>
                  <div className="mt-3 flex flex-wrap gap-1 text-xs">
                    <span className="inline-flex items-center gap-1 text-muted-foreground"><MapPin className="h-3 w-3" /> {v.kota}</span>
                    {v.area_layanan && <span className="text-muted-foreground">· {v.area_layanan}</span>}
                  </div>
                  {v.harga_mulai > 0 && (
                    <p className="mt-3 text-sm">
                      <span className="text-muted-foreground">Mulai </span>
                      <b className="text-primary">{formatRupiah(v.harga_mulai)}</b>
                    </p>
                  )}
                  <div className="mt-auto flex flex-wrap gap-1.5 pt-4">
                    <a data-testid={`vendor-wa-${v.id}`} href={waLink} target="_blank" rel="noreferrer" className="flex-1">
                      <Button className="w-full gap-1 bg-green-600 hover:bg-green-700" size="sm">
                        <MessageCircle className="h-3.5 w-3.5" /> Hubungi
                      </Button>
                    </a>
                    {v.instagram && (
                      <a href={`https://instagram.com/${v.instagram.replace(/^@/, "")}`} target="_blank" rel="noreferrer">
                        <Button size="icon" variant="outline" className="h-9 w-9"><Instagram className="h-4 w-4" /></Button>
                      </a>
                    )}
                    {v.website && (
                      <a href={v.website.startsWith("http") ? v.website : `https://${v.website}`} target="_blank" rel="noreferrer">
                        <Button size="icon" variant="outline" className="h-9 w-9"><Globe className="h-4 w-4" /></Button>
                      </a>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
